import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Factory, Image as ImageIcon, Star } from 'lucide-react';
import type { ManufacturerToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useManufacturersList, useManufacturerMutations } from '~/hooks/use-manufacturers';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Manufacturers (Κατασκευαστές) list — logo + name + code + featured/visible + sort; standalone editor on add/edit. */
export function ManufacturersGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useManufacturersList();
	const { remove } = useManufacturerMutations();

	const [deleteRow, setDeleteRow] = useState<ManufacturerToken | null>(null);

	const columns = useMemo<SystemGridColumn<ManufacturerToken>[]>(
		() => [
			{
				key: 'image',
				header: t('catalog.manufacturers.columns.image'),
				width: '70px',
				toggleable: false,
				render: r =>
					r.img1 ? (
						<img src={r.img1} alt={r.nm ?? ''} className="h-9 w-9 rounded border border-border object-contain" />
					) : (
						<div className="flex h-9 w-9 items-center justify-center rounded border border-border bg-elevated">
							<ImageIcon size={14} className="text-foreground-disabled" />
						</div>
					)
			},
			{
				key: 'name',
				header: t('catalog.manufacturers.columns.name'),
				width: 'minmax(0,1.4fr)',
				sortValue: r => r.nm ?? '',
				exportValue: r => r.nm ?? '',
				render: r => (
					<span className="truncate text-sm font-medium text-foreground" title={r.nm ?? ''}>
						{r.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('catalog.manufacturers.columns.code'),
				width: '160px',
				defaultVisible: false,
				sortValue: r => r.cd ?? '',
				exportValue: r => r.cd ?? '',
				render: r => <span className="truncate font-mono text-xs text-foreground-muted">{r.cd || '—'}</span>
			},
			{
				key: 'featured',
				header: t('catalog.manufacturers.columns.featured'),
				width: '110px',
				align: 'center',
				sortValue: r => (r.isFeatured ? 1 : 0),
				exportValue: r => (r.isFeatured ? t('common.yes') : t('common.no')),
				render: r =>
					r.isFeatured ? (
						<Star size={15} className="text-amber-500" fill="currentColor" />
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'visible',
				header: t('catalog.manufacturers.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.manufacturers.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si ?? 0}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ManufacturerToken>
				gridId="manufacturers"
				title={t('catalog.manufacturers.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => `${r.nm ?? ''} ${r.cd ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Factory size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.manufacturers.add')}
				onAdd={() => navigate('/products/manufacturers/create')}
				onEdit={r => navigate(`/products/manufacturers/${r.id}`)}
				onDelete={setDeleteRow}
				exportFilename="manufacturers"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.manufacturers.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.manufacturers.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
